import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, LOCALE_ID, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { listAnimation } from '@animations/list';
import { skeletonEnter } from '@animations/skeleton-enter';
import { staggerAnimation } from '@animations/stagger';
import {
  Company,
  DurationEnum,
  ExperienceEnum,
  Job,
  StudyLevelEnum,
  toApplication,
  toSource,
  toUtm,
  Utm,
} from '@ats/models';
import { environment } from '@env/environment';
import { JobRoutingEnum } from '@features/job/job-routing.enum';
import { Metas } from '@features/job/job/seo-vars';
import { LocaleEnum } from '@models/commons/locales.enum';
import { RouterState } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import {
  InitStaticData as InitApplicationStaticData,
  SetTmpApplication,
} from '@store/applications/applications.actions';
import { ApplicationsState } from '@store/applications/applications.state';
import { CompaniesState } from '@store/companies/companies.state';
import { GetJob, InitStaticData as InitJobsStaticData } from '@store/jobs/jobs.actions';
import { JobsState } from '@store/jobs/jobs.state';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Component({
  templateUrl: './job.component.html',
  styleUrls: ['./job.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [skeletonEnter(), listAnimation, staggerAnimation()],
})
export class JobComponent implements OnInit, OnDestroy {
  @Select(CompaniesState.authenticatedCompany)
  company$: Observable<Company>;
  job$: Observable<Job>;

  jobRoutingEnum = JobRoutingEnum;
  durationEnum = DurationEnum;
  studyLevelEnum = StudyLevelEnum;
  experienceEnum = ExperienceEnum;

  private readonly destroyed$ = new Subject<void>();
  private metaElements: HTMLMetaElement[] = [];

  constructor(
    private readonly store: Store,
    private readonly metaService: Meta,
    readonly activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(LOCALE_ID) private readonly localeId: string,
    @Inject(PLATFORM_ID) private readonly platformId: string
  ) {
    this.job$ = activatedRoute.paramMap.pipe(
      map(paramMap => paramMap.get('jobId')),
      distinctUntilChanged(),
      tap(jobId => store.dispatch(new GetJob(jobId))),
      switchMap(jobId => store.select(JobsState.job(jobId))),
      shareReplay()
    );
  }

  ngOnInit(): void {
    this.store.dispatch(new InitJobsStaticData());
    this.store.dispatch(new InitApplicationStaticData());

    if (isPlatformBrowser(this.platformId)) {
      this.fillTemporaryUtm();
    }

    combineLatest([this.company$.pipe(filter(company => !!company)), this.job$.pipe(filter(job => !!job))]).subscribe(
      ([company, job]) => {
        this.cleanSeo();

        this.metaElements = [
          this.metaService.addTag({
            name: 'author',
            content: company.name,
          }),
          this.metaService.addTag({
            itemprop: 'name',
            content: job.title,
          }),
          this.metaService.addTag({
            property: 'og:url',
            content: `https://${company._id}.atsii.fr/job/${job._id}`,
          }),
          this.metaService.addTag({
            property: 'og:image',
            content: `${environment.api.googleStorage}/${environment.wizbiiFiles}/${company.logo}`,
          }),
          this.metaService.addTag({
            property: 'og:site_name',
            content: `${company._id}.atsii.fr`,
          }),
          this.metaService.addTag({
            property: 'og:locale',
            content: LocaleEnum[this.localeId],
          }),
          this.metaService.addTag({
            property: 'article:author',
            content: company.name,
          }),
          job.datePublished
            ? this.metaService.addTag({
                property: 'article:published_time',
                content: job.datePublished.toString(),
              })
            : null,
          job.dateModified
            ? this.metaService.addTag({
                property: 'article:modified_time',
                content: job.dateModified.toString(),
              })
            : null,
          this.metaService.addTag({
            property: 'article:publisher',
            content: company.name,
          }),
          this.metaService.addTag({
            itemprop: 'image',
            content: `${environment.api.googleStorage}/${environment.wizbiiFiles}/${company.logo}`,
          }),
          ...this.metaService.addTags(Metas),
        ].filter(elem => elem !== null);
      }
    );
  }

  private cleanSeo() {
    this.metaElements.forEach(meta => {
      this.metaService.removeTagElement(meta);
    });
    this.metaElements = [];
  }

  // tslint:disable-next-line:cognitive-complexity
  private fillTemporaryUtm(): void {
    combineLatest([
      this.store.select(RouterState.state).pipe(
        map((state: any) => Object.keys(state.queryParams).filter(key => key.startsWith('utm_'))),
        distinctUntilChanged()
      ),
      this.store.select(RouterState.state).pipe(
        map((state: any) => state.queryParams),
        distinctUntilChanged()
      ),
      this.store.select(ApplicationsState.sourcesPlatforms).pipe(
        filter(sources => !!sources),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([keys, queryParams]) => keys.map(key => ({ key, value: queryParams[key] }))),
        map(utms => ({
          campaign: utms.find(_utm => _utm.key === 'utm_campaign'),
          content: utms.find(_utm => _utm.key === 'utm_content'),
          medium: utms.find(_utm => _utm.key === 'utm_medium'),
          source: utms.find(_utm => _utm.key === 'utm_source'),
          term: utms.find(_utm => _utm.key === 'utm_term'),
        })),
        map(utm => ({
          campaign: utm.campaign ? utm.campaign.value : null,
          content: utm.content ? utm.content.value : null,
          medium: utm.medium ? utm.medium.value : null,
          source: utm.source ? utm.source.value : null,
          term: utm.term ? utm.term.value : null,
        })),
        switchMap(utm =>
          combineLatest([
            this.store.select(ApplicationsState.sourcePlatform(utm.source)),
            this.store.select(ApplicationsState.sourcePlatform(this.document.referrer)),
          ])
        ),
        map(([sourceUrl, utmReferrer]) =>
          toApplication({
            utmSource: toSource({
              utm: this.buildUtm(sourceUrl ? sourceUrl.utm : null, utmReferrer ? utmReferrer.utm : null),
            }),
          })
        ),
        tap(application => {
          this.store.dispatch(new SetTmpApplication(application));
        }),
        first()
      )
      .subscribe();
  }

  trackByLabel(_, skill: string) {
    return skill;
  }

  // tslint:disable-next-line:cognitive-complexity
  private buildUtm(utmSource?: Utm, utmReferrer?: Utm): Utm {
    return toUtm({
      campaign:
        utmSource && utmSource.campaign
          ? utmSource.campaign
          : utmReferrer && utmReferrer.campaign
          ? utmReferrer.campaign
          : null,
      content:
        utmSource && utmSource.content
          ? utmSource.content
          : utmReferrer && utmReferrer.content
          ? utmReferrer.content
          : null,
      medium:
        utmSource && utmSource.medium
          ? utmSource.medium
          : utmReferrer && utmReferrer.medium
          ? utmReferrer.medium
          : null,
      source:
        utmSource && utmSource.source
          ? utmSource.source
          : utmReferrer && utmReferrer.source
          ? utmReferrer.source
          : null,
      term: utmSource && utmSource.term ? utmSource.term : utmReferrer && utmReferrer.term ? utmReferrer.term : null,
    });
  }

  ngOnDestroy(): void {
    this.cleanSeo();

    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
